import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatEuro } from "@/lib/formatMoney";
import {
  getLieferantEingangsrechnungArtikel,
  type LieferantEingangsrechnungArtikelStat,
} from "@/lib/kontakte";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";

const PREVIEW_LIMIT = 8;

function fmtDate(value?: string | null): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    return `${d}.${m}.${y}`;
  }
  return fmtDateTime(value);
}

function formatMenge(row: LieferantEingangsrechnungArtikelStat): string {
  if (row.menge_summe == null) return "—";
  const qty = row.menge_summe.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  const unit = (row.einheit || "").trim();
  if (!unit || unit === "H87") return qty;
  return `${qty} ${unit}`;
}

export function LieferantEingangsrechnungSection({ geschaeftspartnerId }: { geschaeftspartnerId: number }) {
  const [items, setItems] = useState<LieferantEingangsrechnungArtikelStat[]>([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showAll: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLieferantEingangsrechnungArtikel(geschaeftspartnerId, {
        limit: showAll ? 500 : PREVIEW_LIMIT,
      });
      setItems(res.items || []);
      setTotal(res.total ?? res.items?.length ?? 0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Daten konnten nicht geladen werden.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [geschaeftspartnerId]);

  useEffect(() => {
    setExpanded(false);
    void load(false);
  }, [load]);

  async function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    await load(next);
  }

  const hasMore = total > PREVIEW_LIMIT;

  return (
    <section className="eh-lief-ea overflow-hidden rounded-ios border border-separator bg-card shadow-ios">
      <div className="border-b border-separator bg-card/80 px-4 py-3">
        <h2 className="text-base font-semibold text-label-primary">Eingangsrechnungen (Artikel)</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Aus E-Rechnungen erkannte Positionen — gruppiert nach Artikel mit Häufigkeit und Rechnungszeitraum.
        </p>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : error ? (
          <p className="text-sm text-status-red">{error}</p>
        ) : items.length === 0 ? (
          <p className="py-4 text-sm text-label-secondary">
            Noch keine Eingangsrechnungspositionen für diesen Lieferanten erkannt.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-separator/80">
            <table className="eh-lief-ea-table w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-separator bg-muted/30 text-xs uppercase tracking-wide text-label-secondary">
                  <th className="px-3 py-2.5 font-medium">Artikel</th>
                  <th className="px-3 py-2.5 font-medium text-right">Anzahl</th>
                  <th className="px-3 py-2.5 font-medium text-right">Menge gesamt</th>
                  <th className="px-3 py-2.5 font-medium text-right">Letzter EP netto</th>
                  <th className="px-3 py-2.5 font-medium">Erste Rechnung</th>
                  <th className="px-3 py-2.5 font-medium">Letzte Rechnung</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => {
                  const key = `${row.bezeichnung}|${row.seller_article_nr || ""}|${row.ean || ""}`;
                  return (
                    <tr
                      key={key}
                      className={`border-b border-separator/50 transition-colors hover:bg-accent/5 ${
                        index % 2 === 1 ? "bg-card/40" : ""
                      }`}
                    >
                      <td className="px-3 py-2.5 align-top">
                        <div className="font-medium text-label-primary">{row.bezeichnung}</div>
                        {row.seller_article_nr || row.ean ? (
                          <div className="mt-0.5 text-xs text-label-secondary">
                            {row.seller_article_nr ? `Art.-Nr. ${row.seller_article_nr}` : null}
                            {row.seller_article_nr && row.ean ? " · " : null}
                            {row.ean ? `EAN ${row.ean}` : null}
                          </div>
                        ) : null}
                        {row.stamm_artikel_id ? (
                          <div className="mt-1 inline-flex rounded-full bg-accent/10 px-2 py-0.5 text-xs text-label-secondary">
                            Stamm: {row.stamm_hero_nr || `#${row.stamm_artikel_id}`}
                            {row.stamm_artikel_bezeichnung ? ` – ${row.stamm_artikel_bezeichnung}` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5 text-right align-top tabular-nums">
                        <span className="inline-flex min-w-[1.75rem] justify-center rounded-full bg-muted/50 px-2 py-0.5 text-xs font-medium">
                          {row.bestellungen_anzahl}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right align-top tabular-nums whitespace-nowrap text-label-primary">
                        {formatMenge(row)}
                      </td>
                      <td className="px-3 py-2.5 text-right align-top tabular-nums whitespace-nowrap font-medium text-label-primary">
                        {formatEuro(row.letzter_ep_netto, 2)}
                      </td>
                      <td className="px-3 py-2.5 align-top text-label-secondary whitespace-nowrap">
                        {fmtDate(row.erste_rechnung)}
                      </td>
                      <td className="px-3 py-2.5 align-top text-label-secondary whitespace-nowrap">
                        {fmtDate(row.letzte_rechnung)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && !loading && !error ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-xs text-label-secondary">
              {expanded
                ? `${total.toLocaleString("de-DE")} Artikel angezeigt`
                : `${PREVIEW_LIMIT} von ${total.toLocaleString("de-DE")} Artikeln`}
            </span>
            <Button type="button" size="sm" variant="secondary" onClick={() => void toggleExpanded()}>
              {expanded ? "Weniger anzeigen" : "Alle Positionen anzeigen"}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
